import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { WindowService } from '../utils/window.service';
import { ConfigService } from './config.service';

interface LwaTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  error?: string;
  error_description?: string;
}

@Injectable({ providedIn: 'root' })
export class LwaClientService {
  private static readonly TOKEN_KEY = 'lwa_access_token';
  private static readonly STATE_KEY = 'lwa_oauth_state';
  private static readonly VERIFIER_KEY = 'lwa_pkce_verifier';
  private static readonly LWA_CALLBACK_PATH = '/lwa-login/callback';

  private lwaAuthenticated$ = new BehaviorSubject<boolean>(false);
  private inMemoryToken: string | null = null;
  private sessionStorageAvailable: boolean;

  constructor(
    private configService: ConfigService,
    private windowService: WindowService,
    private httpClient: HttpClient,
    private router: Router,
  ) {
    this.sessionStorageAvailable = this.checkSessionStorageAvailable();
    const existingToken = this.getToken();
    if (existingToken) {
      this.lwaAuthenticated$.next(true);
    }
  }

  /**
   * Redirect the browser to Amazon's authorization endpoint with PKCE.
   * Generates code_verifier, code_challenge, and state for security.
   */
  async startAuthorize(): Promise<void> {
    const clientId = this.configService.json.lwaClientId;
    const authUrl = this.configService.json.lwaAuthorizationUrl;
    if (!clientId || !authUrl) {
      return;
    }

    const state = this.generateRandomString(32);
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    this.storeState(state);
    this.storeVerifier(codeVerifier);

    const redirectUri = this.getRedirectUri();

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'ah::login',
      redirect_uri: redirectUri,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${authUrl}?${params.toString()}`;
  }

  /**
   * Handle the callback after Amazon redirects back with a code.
   * Validates state, then exchanges code for token directly from the browser using PKCE.
   * No client_secret needed — code_verifier replaces it.
   */
  handleCallback(code: string, state: string): Promise<string> {
    const storedState = this.getStoredState();
    const codeVerifier = this.getStoredVerifier();
    this.clearState();
    this.clearVerifier();

    if (!storedState || storedState !== state) {
      return Promise.reject(new Error('Invalid OAuth state. Please try logging in again.'));
    }

    if (!codeVerifier) {
      return Promise.reject(new Error('Missing PKCE verifier. Please try logging in again.'));
    }

    const tokenEndpoint = this.configService.json.lwaTokenEndpoint;
    if (!tokenEndpoint) {
      return Promise.reject(new Error('LWA token endpoint is not configured.'));
    }

    const redirectUri = this.getRedirectUri();
    const clientId = this.configService.json.lwaClientId;

    // POST to Amazon's token endpoint with PKCE code_verifier (no client_secret)
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('client_id', clientId)
      .set('redirect_uri', redirectUri)
      .set('code_verifier', codeVerifier);

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.httpClient
      .post<LwaTokenResponse>(tokenEndpoint, body.toString(), { headers })
      .toPromise()
      .then(response => {
        if (!response || response.error) {
          throw new Error(response?.error_description || response?.error || 'Token exchange failed');
        }
        this.storeToken(response.access_token);
        return response.access_token;
      });
  }

  /** Observable that emits true when an LWA token is present. */
  isLwaAuthenticated$(): Observable<boolean> {
    return this.lwaAuthenticated$.asObservable();
  }

  /** Synchronously retrieve the stored LWA token. */
  getToken(): string | null {
    if (this.sessionStorageAvailable) {
      try {
        return sessionStorage.getItem(LwaClientService.TOKEN_KEY);
      } catch {
        // Fall through to in-memory
      }
    }
    return this.inMemoryToken;
  }

  /** Store an LWA token. */
  storeToken(token: string): void {
    if (this.sessionStorageAvailable) {
      try {
        sessionStorage.setItem(LwaClientService.TOKEN_KEY, token);
      } catch {
        this.inMemoryToken = token;
      }
    } else {
      this.inMemoryToken = token;
    }
    this.lwaAuthenticated$.next(true);
  }

  /** Clear the stored LWA token. */
  clearToken(): void {
    if (this.sessionStorageAvailable) {
      try {
        sessionStorage.removeItem(LwaClientService.TOKEN_KEY);
      } catch {
        // ignore
      }
    }
    this.inMemoryToken = null;
    this.lwaAuthenticated$.next(false);
  }

  /** For auth code flow, refresh requires a full redirect. */
  attemptRefresh(): Promise<string | null> {
    this.clearToken();
    return Promise.resolve(null);
  }

  /** Logout: clear token, navigate to /lwa-login. */
  logout(): void {
    this.clearToken();
    this.router.navigate(['/lwa-login']);
  }

  /** Check if LWA is configured for this environment. */
  isConfigured(): boolean {
    return !!(this.configService.json.lwaClientId && this.configService.json.lwaAuthorizationUrl);
  }

  private getRedirectUri(): string {
    return this.windowService.getLocationOrigin() + LwaClientService.LWA_CALLBACK_PATH;
  }

  /** Generate a cryptographically random string of given byte length, hex-encoded. */
  private generateRandomString(byteLength: number): string {
    const array = new Uint8Array(byteLength);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /** Generate SHA-256 code_challenge from code_verifier (base64url encoded). */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(digest);
  }

  /** Base64url encode an ArrayBuffer (no padding, URL-safe). */
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(b => (binary += String.fromCharCode(b)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private storeState(state: string): void {
    try {
      sessionStorage.setItem(LwaClientService.STATE_KEY, state);
    } catch {
      /* ignore */
    }
  }

  private getStoredState(): string | null {
    try {
      return sessionStorage.getItem(LwaClientService.STATE_KEY);
    } catch {
      return null;
    }
  }

  private clearState(): void {
    try {
      sessionStorage.removeItem(LwaClientService.STATE_KEY);
    } catch {
      /* ignore */
    }
  }

  private storeVerifier(verifier: string): void {
    try {
      sessionStorage.setItem(LwaClientService.VERIFIER_KEY, verifier);
    } catch {
      /* ignore */
    }
  }

  private getStoredVerifier(): string | null {
    try {
      return sessionStorage.getItem(LwaClientService.VERIFIER_KEY);
    } catch {
      return null;
    }
  }

  private clearVerifier(): void {
    try {
      sessionStorage.removeItem(LwaClientService.VERIFIER_KEY);
    } catch {
      /* ignore */
    }
  }

  private checkSessionStorageAvailable(): boolean {
    try {
      const testKey = '__lwa_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      console.warn('sessionStorage is not available. LWA tokens will be stored in memory.');
      return false;
    }
  }
}
